import type { TabContent } from '../components/PageConfigProvider/page-reducer';
import {
  applyTabContentAction,
  type TabContentAction,
} from '../components/PageConfigProvider/page-reducer';
import {
  applyReportFlowAction,
  findItem,
  type ReportFlowAction,
  removeItems,
  replaceItems,
  updateItem,
} from '../components/ReportFlow/report-flow-reducer';
import { emptyReportTabContent } from './report-document-converters';
import type { ReportDocumentConfig } from './report-document-types';

const GRID_ONLY_ACTION_TYPES = [
  'ADD_BLANK_ITEM',
  'SET_GRID',
  'ADD_ROWS',
  'REMOVE_ROWS',
  'ADD_PAGE',
  'DELETE_PAGE_ABOVE',
  'DELETE_PAGE_BELOW',
] as const;

type GridOnlyActionType = (typeof GRID_ONLY_ACTION_TYPES)[number];

export type DocumentTabContentAction = Exclude<
  TabContentAction,
  { type: GridOnlyActionType }
>;

const gridOnlyActionTypes: ReadonlySet<string> = new Set(
  GRID_ONLY_ACTION_TYPES
);

export const isDocumentTabContentAction = (
  action: TabContentAction
): action is DocumentTabContentAction => !gridOnlyActionTypes.has(action.type);

export type ReportDocumentAction = (
  | { type: 'SET_CONFIG'; payload: ReportDocumentConfig }
  | { type: 'UPDATE_NAME'; payload: string }
  | { type: 'UPDATE_TITLE'; payload: string }
  | { type: 'UPDATE_DESCRIPTION'; payload: string }
  | { type: 'FLOW'; action: ReportFlowAction }
  | { type: 'TAB_CONTENT'; action: DocumentTabContentAction }
) & { replacePreviousHistorySnapshot?: boolean };

const scalarsOf = (state: ReportDocumentConfig): TabContent => ({
  ...emptyReportTabContent,
  reportParameters: state.parameters,
  primaryFilter: state.primaryFilter,
  secondaryFilters: state.secondaryFilters ?? [],
  dateRangeDefault: state.dateRangeDefault,
  title: state.title,
  description: state.description,
});

const liftScalars = (
  state: ReportDocumentConfig,
  tabContent: TabContent
): ReportDocumentConfig => ({
  ...state,
  title: tabContent.title,
  description: tabContent.description,
  primaryFilter: tabContent.primaryFilter,
  secondaryFilters: tabContent.secondaryFilters,
  dateRangeDefault: tabContent.dateRangeDefault,
  parameters: tabContent.reportParameters,
});

// Document-level edits; every other tab-content action edits the one item its payload names.
const SCALAR_ACTION_TYPES: ReadonlySet<string> = new Set<
  DocumentTabContentAction['type']
>([
  'UPDATE_PRIMARY_FILTER',
  'SET_SECONDARY_FILTERS',
  'SET_REPORT_PARAMETERS',
  'ADD_REPORT_PARAMETER',
  'UPDATE_REPORT_PARAMETER',
  'REMOVE_REPORT_PARAMETER',
  'UPDATE_REPORT_TITLE',
  'UPDATE_REPORT_DESCRIPTION',
]);

const targetItemId = (action: DocumentTabContentAction): string | undefined => {
  const payload: unknown = action.payload;
  if (typeof payload !== 'object' || payload === null) return undefined;
  const { id, plotId } = payload as { id?: unknown; plotId?: unknown };
  if (typeof id === 'string') return id;
  if (typeof plotId === 'string') return plotId;
  return undefined;
};

const applyDocumentTabContentAction = (
  state: ReportDocumentConfig,
  action: DocumentTabContentAction
): ReportDocumentConfig => {
  if (!isDocumentTabContentAction(action)) return state;
  switch (action.type) {
    case 'SET_ITEMS': {
      const pages = replaceItems(state.pages, action.payload);
      return pages === state.pages ? state : { ...state, pages };
    }
    case 'DELETE_ITEM':
    case 'DELETE_ITEMS': {
      const ids =
        action.type === 'DELETE_ITEM'
          ? [action.payload.id]
          : action.payload.ids;
      const pages = removeItems(state.pages, new Set(ids));
      return pages === state.pages ? state : { ...state, pages };
    }
    default: {
      if (SCALAR_ACTION_TYPES.has(action.type)) {
        return liftScalars(
          state,
          applyTabContentAction(scalarsOf(state), action)
        );
      }
      const itemId = targetItemId(action);
      if (itemId === undefined) {
        console.warn(
          `[report-document] '${action.type}' names no item and is not a document-level action`
        );
        return state;
      }
      const item = findItem(state.pages, itemId);
      if (!item) return state;
      const tabContent = applyTabContentAction(
        { ...scalarsOf(state), items: [item] },
        action
      );
      const updated = tabContent.items.find((entry) => entry.id === itemId);
      const pages =
        updated && updated !== item
          ? updateItem(state.pages, itemId, () => updated)
          : state.pages;
      return liftScalars({ ...state, pages }, tabContent);
    }
  }
};

export const reportDocumentReducer = (
  state: ReportDocumentConfig,
  action: ReportDocumentAction
): ReportDocumentConfig => {
  switch (action.type) {
    case 'SET_CONFIG': {
      return action.payload;
    }
    case 'UPDATE_NAME': {
      return { ...state, name: action.payload };
    }
    case 'UPDATE_TITLE': {
      return { ...state, title: action.payload };
    }
    case 'UPDATE_DESCRIPTION': {
      return { ...state, description: action.payload };
    }
    case 'FLOW': {
      const pages = applyReportFlowAction(state.pages, action.action);
      return pages === state.pages ? state : { ...state, pages };
    }
    case 'TAB_CONTENT': {
      return applyDocumentTabContentAction(state, action.action);
    }
    default: {
      return state;
    }
  }
};
